import React from 'react'
import styled from 'styled'
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"

import * as entity from 'entity'

import { StopWithSpace } from 'domains/org/getty/store'
import { ListItem } from 'domains/org/getty/components/stops/list-item'

import { Stop } from 'generated/mos/org/getty/audio'

import { Placement, SpacePlacement } from 'generated/mos/placement'
import { Space, Site } from 'generated/mos/structure'

import { bffEndpoint } from 'helpers/bff'
import { dndReorder } from 'helpers/drag-and-drop'
import { space } from 'helpers/style'


const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth;
const NewStopsDivider = styled.div`
  position: relative;
  margin-bottom: ${space(3)};
  font-size: ${({ theme }) => theme.font.size.xSmall};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.primary};
  & span {
    position: relative;
    padding-right: ${space(2)};
    background-color: ${({ theme }) => theme.color.grayL90};
  }
  & hr {
    position: absolute;
    top: 50%;
    margin: 0;
    width: calc(50% + 50vw - ${() => getScrollbarWidth() / 2}px);
    border-bottom: 1px solid ${({ theme }) => theme.color.primary};
  }
`;

type Props = {
  readonly projectId: string;
  readonly tourId: string;
  readonly newlyCreatedStopRef?: StopWithSpace['ref'];
  readonly historyPush: (to: string) => void;
  readonly orderedStops: ReadonlyArray<StopWithSpace>;
  readonly setOrderedStops: React.Dispatch<React.SetStateAction<ReadonlyArray<StopWithSpace>>>;
  readonly deleteFromTour?: (stopRef: Stop.Ref) => void;
  readonly spacesListData: readonly Space.Entity[] | undefined;
  readonly sitesData: ReadonlyArray<Site.Entity> | undefined;
};

export const ReorderableStops = ({ orderedStops, setOrderedStops, newlyCreatedStopRef, deleteFromTour, spacesListData, sitesData, ...rest }: Props) => {
  const [hasDragged, setHasDragged] = React.useState(false);
  const lastScrolledFor = React.useRef<entity.Ref>();

  // Scroll to bottom after a stop has been created and loaded && reset
  // hasDragged so divider is visible
  React.useEffect(() => {
    if (!newlyCreatedStopRef) return;
    const newStopHasLoaded = orderedStops.some(
      stop => stop.ref && stop.ref.id === newlyCreatedStopRef.id
    );
    const newStopIsNew = !lastScrolledFor.current
      ? true
      : lastScrolledFor.current.id !== newlyCreatedStopRef.id;

    if (newStopHasLoaded && newStopIsNew) {
      lastScrolledFor.current = newlyCreatedStopRef;
      setHasDragged(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [newlyCreatedStopRef, orderedStops]);

  const isDividerVisible = (index: number) =>
    !hasDragged &&
    newlyCreatedStopRef &&
    orderedStops.length - 1 === index &&
    entity.isRefEqual(orderedStops[index].ref, newlyCreatedStopRef);

  const resolvePlacement = (placement: Placement.Entity) => {
    const resolved = spacesListData && spacesListData.find((item: Space.Entity) => {
      const id = Space.mustRef(item.ref).id
      return placement &&
        placement.type === Placement.refName &&
        placement.placement &&
        placement.placement.type === SpacePlacement.refName &&
        placement.placement.spaceRef &&
        id === placement.placement.spaceRef.id
    })
    const site = resolved && resolved.siteRef && sitesData && sitesData.find(({ ref }: Site.Entity) => resolved.siteRef && Site.mustRef(ref).id === resolved.siteRef.id)
    return {
      placement: resolved ? resolved.displayName || resolved.name : undefined,
      site: site ? site.name : undefined
    }
  }
  const hostName = bffEndpoint(window.location.hostname)

  return (
    <DragDropContext
      onBeforeDragStart={() => !hasDragged && setHasDragged(true)}
      onDragEnd={dropResult => setOrderedStops(dndReorder(dropResult, orderedStops))}
    >
      <Droppable droppableId="stops-list">
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {orderedStops.map((stop, index) => (
              <React.Fragment key={stop.ref && stop.ref.id}>
                {isDividerVisible(index) && (
                  <NewStopsDivider>
                    <hr /><span>Newly added stops</span>
                  </NewStopsDivider>
                )}
                <Draggable draggableId={stop.ref ? stop.ref.id : ''} index={index}>
                  {(provided, snapshot) => {
                    const resolvedLocations = stop.placement && resolvePlacement(stop.placement.value as Placement.Entity)
                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <ListItem
                          {...rest}
                          draggableIndex={index}
                          deleteFromTour={deleteFromTour}
                          isDragging={snapshot.isDragging}
                          site={resolvedLocations && resolvedLocations.site}
                          placement={resolvedLocations && resolvedLocations.placement}
                          stop={stop}
                          hostName={hostName}
                        />
                      </div>
                    )
                    }
                }
                </Draggable>
              </React.Fragment>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

